// Every voice env lookup lives here. Values are baked in at build time (CRA),
// so each environment's env file decides whether browser calling exists.
//
// Kill switch: the feature is on only when REACT_APP_VOICE_ENABLED is exactly
// "true" AND both URLs parse. Anything else turns it fully off (no saga work,
// components render nothing). There are no localhost fallbacks on purpose: a
// build that forgot a key must stay quiet instead of dialling localhost.

const ICE_GATHERING_TIMEOUT_MS = 2000

// Returns the URL without a trailing slash, or null when it is missing, does
// not parse, uses another scheme, or carries a query/hash (paths are appended
// to it, so those would end up in the wrong place).
function parseUrl(value, protocols) {
  if (typeof value !== 'string' || value.trim() === '') return null
  let url
  try {
    url = new URL(value.trim())
  } catch (err) {
    return null
  }
  if (protocols.indexOf(url.protocol) === -1) return null
  if (!url.hostname || url.search || url.hash) return null
  return `${url.protocol}//${url.host}${url.pathname.replace(/\/+$/, '')}`
}

function isIceServer(entry) {
  if (!entry || typeof entry !== 'object' || Array.isArray(entry)) return false
  const { urls, username, credential } = entry
  const urlsOk =
    (typeof urls === 'string' && urls !== '') ||
    (Array.isArray(urls) &&
      urls.length > 0 &&
      urls.every(u => typeof u === 'string' && u !== ''))
  if (!urlsOk) return false
  if (username !== undefined && typeof username !== 'string') return false
  if (credential !== undefined && typeof credential !== 'string') return false
  return true
}

// Optional JSON array of RTCIceServer objects. null means "use the sip.js
// default" (Google STUN). An invalid value falls back to the default and is
// reported as a warning instead of disabling calls.
export function parseIceServers(raw) {
  if (typeof raw !== 'string' || raw.trim() === '') {
    return { value: null, error: null }
  }
  let parsed
  try {
    parsed = JSON.parse(raw)
  } catch (err) {
    parsed = undefined
  }
  if (!Array.isArray(parsed) || !parsed.every(isIceServer)) {
    return {
      value: null,
      error:
        'REACT_APP_SIP_ICE_SERVERS is not a JSON array of RTCIceServer objects; using the sip.js default',
    }
  }
  return { value: parsed, error: null }
}

export function parseVoiceConfig(env) {
  const source = env || {}
  const off = reason => ({ enabled: false, reason, warnings: [] })

  if (source.REACT_APP_VOICE_ENABLED !== 'true') {
    return off('REACT_APP_VOICE_ENABLED is not "true"')
  }
  const gatewayUrl = parseUrl(source.REACT_APP_VOICE_GATEWAY_URL, [
    'http:',
    'https:',
  ])
  if (!gatewayUrl) {
    return off(
      'REACT_APP_VOICE_GATEWAY_URL is missing or is not an http(s) URL',
    )
  }
  const sipWsUrl = parseUrl(source.REACT_APP_SIP_WS_URL, ['ws:', 'wss:'])
  if (!sipWsUrl) {
    return off('REACT_APP_SIP_WS_URL is missing or is not a ws(s) URL')
  }

  const ice = parseIceServers(source.REACT_APP_SIP_ICE_SERVERS)
  return {
    enabled: true,
    reason: null,
    warnings: ice.error ? [ice.error] : [],
    gatewayUrl,
    // http -> ws, https -> wss (browsers cannot send headers on a WebSocket,
    // so /ws/user gets the token as ?token=).
    gatewayWsUrl: gatewayUrl.replace(/^http/, 'ws'),
    sipWsUrl,
    iceServers: ice.value,
    iceGatheringTimeout: ICE_GATHERING_TIMEOUT_MS,
  }
}

// Read key by key: a bare `process.env` would inline every REACT_APP_* value
// into this chunk.
export const VOICE_CONFIG = parseVoiceConfig({
  REACT_APP_VOICE_ENABLED: process.env.REACT_APP_VOICE_ENABLED,
  REACT_APP_VOICE_GATEWAY_URL: process.env.REACT_APP_VOICE_GATEWAY_URL,
  REACT_APP_SIP_WS_URL: process.env.REACT_APP_SIP_WS_URL,
  REACT_APP_SIP_ICE_SERVERS: process.env.REACT_APP_SIP_ICE_SERVERS,
})

export function isVoiceEnabled() {
  return VOICE_CONFIG.enabled
}

let warned = false

// Called once from the root saga. A disabled build logs exactly one warning.
export function warnVoiceConfigOnce(config = VOICE_CONFIG) {
  if (warned) return
  warned = true
  if (!config.enabled) {
    console.warn(`[voice] browser calling is off: ${config.reason}`)
    return
  }
  config.warnings.forEach(w => console.warn(`[voice] ${w}`))
}
